import { useMemo } from "react";
import { useAppSelector } from "../../app";
import useAttentionRequired from "./useAttentionRequired";
import { IssueLine, IssuesEntity } from "../interfaces/issuesEntity";
import { SHOW_ISSUES_STATUS_LIST } from "../constants";
import { useMultipleGroupBlocks } from "../../configMap";
import { useTranslation } from "react-i18next";
import { getInLanguageOrDefault } from "../../common/utilities/language";
import selectAnnotationReducer from "../../annotation/selectors/annotationReducerSelector";

const useIssuesHeaderTag = (disabled: boolean) => {
  const groupBlocks = useAppSelector(
    (state) => state.configMapReducer.configMap?.groupBlocks,
  );

  const { activeDocumentSet } = useAppSelector(
    (state) => state.documentSetsReducer,
  );

  const enforceRequiredEntities = useAppSelector(
    (state) =>
      state.documentReducer.activeDocument?.documentType
        ?.enforceRequiredEntities,
  );

  const annotations = useAppSelector(selectAnnotationReducer);

  const incompleteEntities = useAppSelector(
    (state) => state.metadataReducer.incompleteEntities,
  );

  const { countPerGroupBlockId } = useMultipleGroupBlocks();

  const {
    t,
    i18n: { language },
  } = useTranslation("errors");

  const issueEntities = useMemo(() => {
    if (!groupBlocks?.length) {
      return [];
    }

    const entities: Array<IssuesEntity> = groupBlocks.flatMap((gb) =>
      gb.groupBlockEntityTypes.map((entity) => ({
        entity: entity,
        entityId: entity.entityType.id,
        entityIndex: entity.index,
        entityTranslations: entity.entityType.translations,
        groupBlockId: gb.id,
        groupBlockIndex: gb.index,
        multipleGroupBlocks: gb.multipleGroupBlocks,
        groupBlockName: gb.translations,
      })),
    );

    if (!entities?.length) {
      return [];
    }

    return entities;
  }, [groupBlocks]);

  const entityIds = useMemo(() => {
    if (!issueEntities?.length) {
      return [];
    }

    return issueEntities.map((entity) => entity.entityId);
  }, [issueEntities]);

  const {
    errorAttentionRequired,
    scoreAttentionRequired,
    errorAttentionAnnotations,
    scoreAttentionAnnotations,
  } = useAttentionRequired(entityIds);

  const missingRequiredEntityLines: Array<IssueLine> = useMemo(() => {
    if (
      !enforceRequiredEntities ||
      !groupBlocks?.length ||
      !incompleteEntities.length
    ) {
      return [];
    }

    const lines: Array<IssueLine> = [];

    groupBlocks.forEach((groupBlock) => {
      const requiredEntityTypes = groupBlock.groupBlockEntityTypes.filter(
        (gbet) => gbet.required,
      );

      if (!requiredEntityTypes.length) {
        return;
      }

      const groupBlockItemCount = groupBlock.multipleGroupBlocks
        ? countPerGroupBlockId(groupBlock.id)
        : 1;

      const groupBlockName = getInLanguageOrDefault(
        groupBlock.translations,
        language,
      );

      requiredEntityTypes.forEach((gbet) => {
        for (let i = 1; i <= groupBlockItemCount; i++) {
          const isFlaggedAsIncomplete = incompleteEntities.some(
            (be) => be.entityTypeId === gbet.entityType.id && be.index === i,
          );

          if (!isFlaggedAsIncomplete) {
            continue;
          }

          const hasAnnotation = annotations.some(
            (annotation) =>
              annotation.entity?.id === gbet.entityType.id &&
              annotation.index === i &&
              !annotation.tempAnnotation,
          );

          if (!hasAnnotation) {
            const entityName = getInLanguageOrDefault(
              gbet.entityType.translations,
              language,
            );
            const lineName = groupBlock.multipleGroupBlocks
              ? `${entityName} - Line ${i}`
              : entityName;

            lines.push({
              entityType: { ...gbet, index: i },
              entityIndex: gbet.index,
              index: i,
              groupBlockId: groupBlock.id,
              multipleGroupBlocks: groupBlock.multipleGroupBlocks,
              entityName: lineName,
              groupBlockName,
              groupBlockIndex: groupBlock.index,
              isError: true,
              issueReason: t("issues.missingRequired"),
            });
          }
        }
      });
    });

    return lines;
  }, [
    enforceRequiredEntities,
    groupBlocks,
    annotations,
    incompleteEntities,
    countPerGroupBlockId,
    language,
    t,
  ]);

  const hasMissingRequiredEntities = missingRequiredEntityLines.length > 0;

  const tagColor = useMemo(() => {
    if (errorAttentionRequired || hasMissingRequiredEntities) {
      return "error";
    }
    if (scoreAttentionRequired) {
      return "warning";
    }
    return "default";
  }, [
    errorAttentionRequired,
    scoreAttentionRequired,
    hasMissingRequiredEntities,
  ]);

  const areThereIssues = useMemo(() => {
    if (
      disabled ||
      !activeDocumentSet ||
      !SHOW_ISSUES_STATUS_LIST.includes(activeDocumentSet.status)
    ) {
      return false;
    }

    return (
      errorAttentionRequired ||
      scoreAttentionRequired ||
      hasMissingRequiredEntities
    );
  }, [
    errorAttentionRequired,
    scoreAttentionRequired,
    hasMissingRequiredEntities,
    activeDocumentSet,
    disabled,
  ]);

  const issuesTagHeight = useMemo(() => {
    return areThereIssues ? 30 : 0;
  }, [areThereIssues]);

  return {
    errorAttentionAnnotations,
    scoreAttentionAnnotations,
    missingRequiredEntityLines,
    tagColor,
    issueEntities,
    areThereIssues,
    issuesTagHeight,
  };
};

export default useIssuesHeaderTag;
